import axios from 'axios';
import { api } from '../api';
import { queryClient } from '../query-client';
import { useAuthStore } from '../auth-store';
import { OFFLINE_TOKEN, useOfflineStore } from './state';
import { dequeue, markStuck, queueRecords } from './store';
import type { QueueRecord } from './types';

const direct = { networkOnly: true } as Record<string, unknown>;

let running: Promise<void> | null = null;

/**
 * Send what is waiting, oldest first and sales before shift closes, so a shift's
 * expected cash includes the sales in it. Safe to call any time and from any
 * number of tabs: the server keys every record, so a repeat changes nothing.
 *
 * The record leaves this device only once the server has it. A failed sale is
 * still the server's to hold (it answers 200 and keeps it for an admin), so only
 * a refusal of the request itself stays here for a person to look at.
 */
export function syncNow(): Promise<void> {
  if (!running) {
    running = doSync().finally(() => {
      running = null;
    });
  }
  return running;
}

const soldAtOf = (r: QueueRecord) => (r.kind === 'sale' ? r.sale.soldAt : r.shift.closedAt);

async function doSync(): Promise<void> {
  const store = useOfflineStore.getState();
  const token = useAuthStore.getState().token;
  if (!store.online || !token || token === OFFLINE_TOKEN) return;

  const rows = await queueRecords();
  if (rows.length === 0) return;
  const ordered = [
    ...rows.filter((r) => r.kind === 'sale').sort((a, b) => soldAtOf(a).localeCompare(soldAtOf(b))),
    ...rows
      .filter((r) => r.kind === 'shift_close')
      .sort((a, b) => soldAtOf(a).localeCompare(soldAtOf(b))),
  ];

  store.set({ syncing: true });
  try {
    for (const record of ordered) {
      try {
        if (record.kind === 'sale') {
          await api.post('/pos/offline/sales', record.sale, direct);
        } else {
          await api.post('/pos/offline/shift-closes', record.shift, direct);
        }
        await dequeue(record.id);
      } catch (err) {
        if (!axios.isAxiosError(err) || !err.response) return; // The connection dropped: try later.
        const status = err.response.status;
        if (status === 401) {
          useOfflineStore.getState().set({ needsSignIn: true });
          return;
        }
        if (status >= 500) return; // The server's trouble, not this sale's.
        // Refused outright. Keep it and carry on, so one bad record can't hold
        // up every sale behind it.
        const code = (err.response.data as { error?: string } | undefined)?.error;
        await markStuck(record, code ?? `http_${status}`);
      }
    }
  } finally {
    useOfflineStore.getState().set({ syncing: false });
    void queryClient.invalidateQueries();
  }
}
